import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Loader2,
  Pencil,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  adminDeleteInsurerKpi,
  adminListInsurerKpis,
  adminParseInsurerKpiUpload,
  adminSaveInsurerKpi,
  adminSaveInsurerKpiBatch,
  adminUpdateInsurerKpi,
} from "../../lib/adminApi";
import { normalizeDetail } from "../../lib/errors";

/**
 * Admin > Insurer KPIs.
 *
 * Two workflows:
 *   1. **Upload IRDAI report** (PDF or CSV, up to 3 files). Gemini parses insurer-wise KPIs;
 *      admin reviews the previewed rows and hits Save to bulk-upsert.
 *   2. **Manual add / edit** — for one-off corrections.
 *
 * The table populates the "Insurer KPIs" section shown on every Health Policy Analysis.
 */
const EMPTY_ROW = {
  name: "",
  aliases: "",
  csr: "",
  icr: "",
  solvency_ratio: "",
  complaint_ratio: "",
  network_hospitals: "",
  fiscal_year: "",
};

function fmt(n, digits = 1) {
  if (n == null || n === "") return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return v.toFixed(digits);
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function AdminInsurerKpisPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // id of row being edited
  const [formValue, setFormValue] = useState(EMPTY_ROW);

  // Upload/parse state
  const [files, setFiles] = useState([]);
  const [fiscalYear, setFiscalYear] = useState("FY24-25");
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState(0);
  const [preview, setPreview] = useState(null); // {rows, fiscal_year, source}
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await adminListInsurerKpis());
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load KPIs."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  function beginEdit(row) {
    setEditing(row.id);
    setShowForm(true);
    setFormValue({
      ...EMPTY_ROW,
      ...row,
      aliases: (row.aliases || []).join(", "),
      csr: row.csr ?? "",
      icr: row.icr ?? "",
      solvency_ratio: row.solvency_ratio ?? "",
      complaint_ratio: row.complaint_ratio ?? "",
      network_hospitals: row.network_hospitals ?? "",
      fiscal_year: row.fiscal_year || "",
    });
  }

  function beginAdd() {
    setEditing(null);
    setFormValue(EMPTY_ROW);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditing(null);
    setFormValue(EMPTY_ROW);
  }

  async function handleSaveForm(e) {
    e.preventDefault();
    if (!formValue.name.trim()) {
      toast.error("Insurer name is required.");
      return;
    }
    const payload = {
      ...formValue,
      aliases: formValue.aliases
        ? formValue.aliases.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    };
    try {
      if (editing) await adminUpdateInsurerKpi(editing, payload);
      else await adminSaveInsurerKpi(payload);
      toast.success(editing ? "Updated." : "Added.");
      cancelForm();
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save."));
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this insurer's KPI row?")) return;
    try {
      await adminDeleteInsurerKpi(id);
      toast.success("Deleted.");
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't delete."));
    }
  }

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of incoming) {
        if (merged.length >= 3) {
          toast.error("Upload at most 3 files at once.");
          break;
        }
        if (merged.some((m) => m.name === f.name && m.size === f.size)) continue;
        merged.push(f);
      }
      return merged;
    });
  }

  async function handleParse() {
    if (!files.length) {
      toast.error("Choose at least one IRDAI PDF or CSV file first.");
      return;
    }
    setParsing(true);
    setParseProgress(0);
    setPreview(null);
    try {
      const res = await adminParseInsurerKpiUpload({
        files,
        fiscalYear,
        onProgress: setParseProgress,
      });
      setPreview(res);
      if (!res.rows?.length) {
        toast.warning("Gemini did not find any insurer rows in the uploaded files.");
      } else {
        toast.success(`Extracted ${res.rows.length} insurer rows — review and save.`);
      }
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Extraction failed."));
    } finally {
      setParsing(false);
    }
  }

  async function handleSaveBatch() {
    if (!preview?.rows?.length) return;
    setSaving(true);
    try {
      const res = await adminSaveInsurerKpiBatch({
        source: preview.source || "IRDAI upload",
        rows: preview.rows,
      });
      toast.success(
        `Saved · created ${res.created} · updated ${res.updated} · skipped ${res.skipped}.`,
      );
      setPreview(null);
      setFiles([]);
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save batch."));
    } finally {
      setSaving(false);
    }
  }

  const sorted = useMemo(() => [...rows].sort((a, b) => a.name.localeCompare(b.name)), [rows]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="admin-insurer-kpis-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted hover:text-gold">
        <ArrowLeft className="h-3 w-3" /> Admin dashboard
      </Link>
      <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold">Master Data</div>
          <h1 className="mt-1 font-serif text-3xl text-text">Insurer KPIs</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Latest IRDAI numbers per health insurer. Populated from the annual report or entered
            manually. Shown on every Health Policy Analysis under the &quot;Insurer KPIs&quot;
            section.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={beginAdd} data-testid="add-kpi-manual">
            <Plus className="mr-2 h-4 w-4" /> Add insurer
          </Button>
        </div>
      </div>

      {/* Upload / parse card */}
      <Card className="mb-6 border-gold/30" data-testid="kpi-upload-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-4 w-4 text-gold" /> Upload IRDAI report
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <label
              htmlFor="kpi-file"
              className={`flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed border-border bg-elevated/40 px-6 py-6 text-center hover:border-gold/50 ${
                parsing ? "opacity-60 pointer-events-none" : ""
              }`}
              data-testid="kpi-drop-zone"
            >
              <Upload className="h-5 w-5 text-gold" />
              <span className="text-sm text-text">
                {files.length === 0
                  ? "Click to add PDF, CSV or Excel files (up to 3, 20MB each)"
                  : `${files.length} file${files.length > 1 ? "s" : ""} selected`}
              </span>
              <span className="text-[11px] text-muted">
                Gemini will parse insurer-wise CSR / ICR / Solvency / Complaint ratio / Network
                hospital numbers.
              </span>
              <input
                id="kpi-file"
                type="file"
                multiple
                accept="application/pdf,.pdf,.csv,text/csv,text/plain,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
                className="hidden"
                disabled={parsing}
                data-testid="kpi-file-input"
              />
            </label>
            <div className="flex flex-col gap-2 sm:min-w-[180px]">
              <Label htmlFor="kpi-fy">Fiscal year</Label>
              <Input
                id="kpi-fy"
                value={fiscalYear}
                onChange={(e) => setFiscalYear(e.target.value)}
                placeholder="e.g. FY24-25"
                disabled={parsing}
                data-testid="kpi-fy-input"
              />
              <Button onClick={handleParse} disabled={parsing || files.length === 0} data-testid="kpi-parse">
                {parsing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {parseProgress < 100 ? `Uploading ${parseProgress}%` : "Gemini parsing…"}
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" /> Extract with AI
                  </>
                )}
              </Button>
            </div>
          </div>

          {files.length > 0 && (
            <ul className="flex flex-col gap-1" data-testid="kpi-file-list">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${f.size}`}
                  className="flex items-center justify-between rounded-md border border-border bg-elevated/40 px-3 py-1.5 text-xs text-text"
                >
                  <span className="truncate">{f.name} ({(f.size / 1024 / 1024).toFixed(2)}MB)</span>
                  <button
                    type="button"
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="ml-2 text-muted hover:text-red-400"
                    disabled={parsing}
                    data-testid={`kpi-file-remove-${i}`}
                    aria-label={`Remove ${f.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Preview */}
          {preview?.rows && (
            <div className="mt-4 rounded-md border border-gold/40 bg-gold/5 p-4" data-testid="kpi-preview">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-text">
                    Preview · {preview.rows.length} insurer{preview.rows.length !== 1 && "s"} extracted
                  </div>
                  <div className="text-[11px] text-muted">
                    {preview.source} · {preview.fiscal_year || "FY not detected"}
                  </div>
                </div>
                <Button onClick={handleSaveBatch} disabled={saving || !preview.rows.length} data-testid="kpi-save-batch">
                  {saving ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="mr-2 h-4 w-4" /> Save all ({preview.rows.length})</>
                  )}
                </Button>
              </div>
              <KpiTable rows={preview.rows} readOnly />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual edit form */}
      {showForm && (
        <Card className="mb-6" data-testid="kpi-manual-form">
          <CardHeader>
            <CardTitle className="text-base">
              {editing ? "Edit insurer" : "Add insurer manually"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveForm} className="grid gap-3 sm:grid-cols-2">
              {[
                { key: "name", label: "Insurer name", required: true, testid: "kpi-form-name" },
                { key: "aliases", label: "Aliases (comma-separated)", testid: "kpi-form-aliases" },
                { key: "csr", label: "CSR (%)", type: "number", testid: "kpi-form-csr" },
                { key: "icr", label: "ICR (%)", type: "number", testid: "kpi-form-icr" },
                { key: "solvency_ratio", label: "Solvency ratio", type: "number", testid: "kpi-form-solvency" },
                { key: "complaint_ratio", label: "Complaint ratio (per 10K)", type: "number", testid: "kpi-form-complaints" },
                { key: "network_hospitals", label: "Network hospitals", type: "number", testid: "kpi-form-network" },
                { key: "fiscal_year", label: "Fiscal year", testid: "kpi-form-fy" },
              ].map((f) => (
                <div key={f.key} className="flex flex-col gap-1.5">
                  <Label htmlFor={`kpi-${f.key}`}>{f.label}</Label>
                  <Input
                    id={`kpi-${f.key}`}
                    type={f.type || "text"}
                    step={f.type === "number" ? "0.01" : undefined}
                    required={f.required}
                    value={formValue[f.key] ?? ""}
                    onChange={(e) => setFormValue({ ...formValue, [f.key]: e.target.value })}
                    data-testid={f.testid}
                  />
                </div>
              ))}
              <div className="col-span-full flex gap-2">
                <Button type="submit" data-testid="kpi-form-save">
                  <Save className="mr-2 h-4 w-4" /> {editing ? "Update" : "Save"}
                </Button>
                <Button type="button" variant="outline" onClick={cancelForm} data-testid="kpi-form-cancel">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Data table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-gold" /> All insurers ({sorted.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-center text-sm text-muted">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted" data-testid="kpi-empty">
              No insurers yet. Upload an IRDAI report or add manually.
            </p>
          ) : (
            <KpiTable rows={sorted} onEdit={beginEdit} onDelete={handleDelete} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiTable({ rows, onEdit, onDelete, readOnly = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] text-left text-xs">
        <thead className="bg-elevated/40 text-[10px] uppercase tracking-widest text-muted">
          <tr>
            <th className="px-3 py-2">Insurer</th>
            <th className="px-3 py-2 text-right">CSR</th>
            <th className="px-3 py-2 text-right">ICR</th>
            <th className="px-3 py-2 text-right">Solvency</th>
            <th className="px-3 py-2 text-right">Complaints</th>
            <th className="px-3 py-2 text-right">Network</th>
            <th className="px-3 py-2">FY</th>
            {!readOnly && <th className="px-3 py-2">Updated</th>}
            {!readOnly && <th className="px-3 py-2 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => (
            <tr key={r.id || r.name_slug || `${r.name}-${i}`} className="hover:bg-elevated/30" data-testid={`kpi-row-${r.id || i}`}>
              <td className="px-3 py-2">
                <div className="text-text">{r.name}</div>
                {r.aliases?.length ? (
                  <div className="text-[10px] text-muted">{r.aliases.join(" · ")}</div>
                ) : null}
              </td>
              <td className="px-3 py-2 text-right font-mono">{fmt(r.csr)}%</td>
              <td className="px-3 py-2 text-right font-mono">{fmt(r.icr)}%</td>
              <td className="px-3 py-2 text-right font-mono">{fmt(r.solvency_ratio, 2)}</td>
              <td className="px-3 py-2 text-right font-mono">{fmt(r.complaint_ratio, 1)}</td>
              <td className="px-3 py-2 text-right font-mono">
                {r.network_hospitals != null ? r.network_hospitals.toLocaleString("en-IN") : "—"}
              </td>
              <td className="px-3 py-2 text-muted">{r.fiscal_year || "—"}</td>
              {!readOnly && (
                <td className="px-3 py-2 text-muted">{formatDate(r.last_updated)}</td>
              )}
              {!readOnly && (
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onEdit(r)}
                    className="mr-2 rounded-md p-1 text-muted hover:text-gold"
                    aria-label="Edit"
                    data-testid={`kpi-edit-${r.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(r.id)}
                    className="rounded-md p-1 text-muted hover:text-red-400"
                    aria-label="Delete"
                    data-testid={`kpi-delete-${r.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminInsurerKpisPage;
